import { useCallback } from 'react';

interface ReversedMultiReferenceState {
    changes: any[];
    addChange: (fieldPath: string, action: 'add' | 'remove' | 'update', item: any, index?: number) => void;
    getChangesForField: (fieldPath: string) => any[];
    clearChangesForField: (fieldPath: string) => void;
    clearAllChanges: () => void;
    getAllChanges: () => any[];
}

// Global state to track all multi-reference changes
let globalReversedMultiReferenceState: any[] = [];

export const useReversedMultiReferenceState = (): ReversedMultiReferenceState => {
    const addChange = useCallback((fieldPath: string, action: 'add' | 'remove' | 'update', item: any) => {
        // Find existing entry for this fieldPath
        const existingIndex = globalReversedMultiReferenceState.findIndex(change => change[fieldPath]);

        const itemChange = { ...item, action__: action };

        if (existingIndex !== -1) {
            const fieldChanges = globalReversedMultiReferenceState[existingIndex][fieldPath];

            // Check if this item already exists in the changes
            const existingItemIndex = fieldChanges.findIndex((existingChange: any) => {
                // Compare items by primaryKey__ if available, otherwise by full object comparison
                if (item.primaryKey__ && existingChange.primaryKey__) {
                    return item.primaryKey__ === existingChange.primaryKey__;
                }
                // Fallback to deep comparison for items without primaryKey__
                return JSON.stringify({ ...item, action__: undefined }) ===
                    JSON.stringify({ ...existingChange, action__: undefined });
            });

            if (existingItemIndex !== -1) {
                const existingChange = fieldChanges[existingItemIndex];

                // Consolidate actions based on the rule: add + update = add
                if (existingChange.action__ === 'add' && action === 'update') {
                    // Keep the add action, but update the item data
                    fieldChanges[existingItemIndex] = {...itemChange, action__: 'add'};
                } else if (existingChange.action__ === 'update' && action === 'add') {
                    // Change update to add action
                    fieldChanges[existingItemIndex] = itemChange;
                } else if (existingChange.action__ === 'add' && action === 'remove') {
                    // Remove the item entirely (add + remove = no change)
                    fieldChanges.splice(existingItemIndex, 1);
                } else if (existingChange.action__ === 'remove' && action === 'add') {
                    // Change remove to update (since item was removed then added back)
                    fieldChanges[existingItemIndex] = { ...itemChange, action__: 'update' };
                } else {
                    // For other cases (update + update, remove + remove, etc.), replace with new action
                    fieldChanges[existingItemIndex] = itemChange;
                }
            } else {
                // Item doesn't exist, add it
                fieldChanges.push(itemChange);
            }
        } else {
            // Create new entry with array
            globalReversedMultiReferenceState.push({[fieldPath]: [itemChange]});
        }
    }, []);

    const getChangesForField = useCallback((fieldPath: string): any[] => {
        const fieldEntry = globalReversedMultiReferenceState.find(change => change[fieldPath]);
        return fieldEntry ? fieldEntry[fieldPath] : [];
    }, []);

    const clearChangesForField = useCallback((fieldPath: string) => {
        globalReversedMultiReferenceState = globalReversedMultiReferenceState.filter(
            change => !change[fieldPath]
        );
    }, []);

    const clearAllChanges = useCallback(() => {
        globalReversedMultiReferenceState = [];
    }, []);

    const getAllChanges = useCallback((): any[] => {
        return [...globalReversedMultiReferenceState];
    }, []);

    return {
        changes: globalReversedMultiReferenceState,
        addChange,
        getChangesForField,
        clearChangesForField,
        clearAllChanges,
        getAllChanges
    };
};

// Export the global state for direct access if needed
export const getGlobalReversedMultiReferenceState = (): any[] => {
    return [...globalReversedMultiReferenceState];
};

// Export function to clear the global state directly
export const clearGlobalReversedMultiReferenceState = (): void => {
    globalReversedMultiReferenceState = [];
};
